/**
 * Opening-sketch (door/window) annotation properties — registration and getters.
 *
 * An OPENING_SKETCH is a door/window drawn as a line on top of a WALL_SKETCH
 * edge (layout persona), as opposed to the floorplan OPENING which is a free
 * 4-corner polygon. Its geometry is parametric on the wall edge:
 *   - openingsketch_wall_edge_id (STRING)   — WallSketchEdgeData.id it sits on
 *   - openingsketch_offset (FLOAT)          — distance along the edge from the
 *                                              start node to the opening's near end
 *   - openingsketch_length (FLOAT)          — span along the wall (UI: "width")
 *   - openingsketch_lower_bound (FLOAT)     — sill height / ground offset (absolute Z)
 *   - openingsketch_height (FLOAT)          — vertical size ABOVE the ground
 *                                              offset (head Z = lower + height)
 *   - openingsketch_inset (FLOAT)           — how far the glyph sits INWARD from
 *                                              the outer wall face on its side
 *                                              (≥0; converted to a signed
 *                                              centerline offset at glyph time)
 * Glyph params (replicated from the source floorplan OPENING on creation):
 *   - openingsketch_kind (SELECT DOOR|WINDOW)
 *   - openingsketch_door_type (SELECT)      — a DoorType or an organisation door (`c_<id>`)
 *   - openingsketch_window_type (SELECT)    — STANDARD or an organisation window (`c_<id>`)
 *   - openingsketch_hinge_at_start (BOOLEAN)
 *   - openingsketch_swing_positive (BOOLEAN)
 *   - openingsketch_rollable (BOOLEAN)
 * Linkage:
 *   - openingsketch_source_opening_id (STRING) — identifier of the floorplan
 *     OPENING this was replicated from (keeps them linked + dedupes replication)
 */

import { AnnotationType, PropertyType, PropertyValueType, type PropertyConfig } from '../../types';
import { propertyRegistry } from '../../PropertyRegistry';
import { ReactiveProperty } from '../../reactive/ReactiveProperty';
import { getGlobalPropertyService } from '../../reactive/globalPropertyService';
import { OpeningKind, DoorType, STANDARD_WINDOW_TYPE, type DoorGlyphType, type WindowGlyphType } from 'shared-types';

type SelectOption = NonNullable<NonNullable<PropertyConfig['validation']>['options']>[number];

/** ConvertType.DISTANCE — drives the unit suffix + meter/feet conversion on
 *  distance float inputs (mirrors the value used across the other getters). */
const CONVERT_TYPE_DISTANCE = 1;

export function registerOpeningSketchProperties() {
  // --- linkage to the wall-sketch edge (internal) ---
  if (!propertyRegistry.isRegistered('openingsketch_wall_edge_id')) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_GLOBAL,
      identifier: 'openingsketch_wall_edge_id',
      name: 'wall edge',
      valueType: PropertyValueType.STRING,
      defaultValue: '',
      annotationTypes: [AnnotationType.OPENING_SKETCH],
      description: 'Identifier (WallSketchEdgeData.id) of the wall edge this opening sits on',
      ui: { hidden: true },
    });
  }

  // --- parametric geometry along/around the edge ---
  if (!propertyRegistry.isRegistered('openingsketch_offset')) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_GLOBAL,
      identifier: 'openingsketch_offset',
      name: 'offset',
      valueType: PropertyValueType.FLOAT,
      defaultValue: 0,
      annotationTypes: [AnnotationType.OPENING_SKETCH],
      description: 'Distance along the wall edge from its start node to the opening near end (m)',
      // Hidden from the panel — driven by the on-canvas slide handle.
      ui: { group: 'information', order: 10, decimals: 2, useUserPreferenceConvertType: CONVERT_TYPE_DISTANCE, hidden: true },
    });
  }

  if (!propertyRegistry.isRegistered('openingsketch_length')) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_GLOBAL,
      identifier: 'openingsketch_length',
      name: 'width',
      valueType: PropertyValueType.FLOAT,
      defaultValue: 0.9,
      annotationTypes: [AnnotationType.OPENING_SKETCH],
      description: 'Span of the opening along the wall (m)',
      // Kind-agnostic, so it stays visible for mixed door+window selections
      // (group view) — the whole point of batch-editing openings.
      ui: { group: 'information', order: 11, decimals: 2, useUserPreferenceConvertType: CONVERT_TYPE_DISTANCE },
    });
  }

  if (!propertyRegistry.isRegistered('openingsketch_lower_bound')) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_GLOBAL,
      identifier: 'openingsketch_lower_bound',
      name: 'ground offset',
      valueType: PropertyValueType.FLOAT,
      defaultValue: 0,
      annotationTypes: [AnnotationType.OPENING_SKETCH],
      description: 'Sill height of the opening (absolute Z, m)',
      // Kind-agnostic — visible in group view for batch edits.
      ui: { group: 'information', order: 13, decimals: 2, useUserPreferenceConvertType: CONVERT_TYPE_DISTANCE },
    });
  }

  if (!propertyRegistry.isRegistered('openingsketch_height')) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_GLOBAL,
      identifier: 'openingsketch_height',
      name: 'height',
      valueType: PropertyValueType.FLOAT,
      defaultValue: 2.1,
      annotationTypes: [AnnotationType.OPENING_SKETCH],
      description: 'Vertical size of the opening above its ground offset (m). Head Z = ground offset + height.',
      // Kind-agnostic — visible in group view for batch edits.
      ui: { group: 'information', order: 12, decimals: 2, useUserPreferenceConvertType: CONVERT_TYPE_DISTANCE },
    });
  }

  if (!propertyRegistry.isRegistered('openingsketch_inset')) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_GLOBAL,
      identifier: 'openingsketch_inset',
      name: 'inset',
      valueType: PropertyValueType.FLOAT,
      defaultValue: 0,
      annotationTypes: [AnnotationType.OPENING_SKETCH],
      // Non-negative distance the glyph sits INWARD from the outer wall face on
      // the Side it's on. The glyph builders convert this to a signed centerline
      // offset using the edge's face distance + `swing_positive` (see
      // signedPerpFromInset). Renders natively in the panel like the others.
      description: 'Distance the door/window symbol sits inward from the outer wall face on its side (m)',
      validation: { min: 0 },
      // Kind-agnostic — visible in group view for batch edits.
      ui: { group: 'information', order: 14, decimals: 2, useUserPreferenceConvertType: CONVERT_TYPE_DISTANCE },
    });
  }

  // --- glyph params (replicated from the floorplan OPENING) ---
  if (!propertyRegistry.isRegistered('openingsketch_kind')) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_GLOBAL,
      identifier: 'openingsketch_kind',
      name: 'type',
      valueType: PropertyValueType.SELECT,
      defaultValue: OpeningKind.DOOR,
      annotationTypes: [AnnotationType.OPENING_SKETCH],
      description: 'Subtype of the opening: door or window',
      ui: { group: 'information', order: 1, hiddenInGroup: true },
      validation: {
        options: [
          { value: OpeningKind.DOOR, label: 'door' },
          { value: OpeningKind.WINDOW, label: 'window' },
        ],
      },
    });
  }

  if (!propertyRegistry.isRegistered('openingsketch_door_type')) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_GLOBAL,
      identifier: 'openingsketch_door_type',
      name: 'door type',
      valueType: PropertyValueType.SELECT,
      defaultValue: DoorType.SINGLE,
      annotationTypes: [AnnotationType.OPENING_SKETCH],
      description: 'Door subtype, drawn with the matching floor-plan glyph',
      ui: {
        group: 'information',
        order: 4,
        hiddenInGroup: true,
        visibilityConditions: [{ propertyIdentifier: 'openingsketch_kind', value: OpeningKind.DOOR }],
      },
      validation: { options: [...BUILTIN_DOOR_TYPE_OPTIONS] },
    });
  }

  if (!propertyRegistry.isRegistered('openingsketch_window_type')) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_GLOBAL,
      identifier: 'openingsketch_window_type',
      name: 'window type',
      valueType: PropertyValueType.SELECT,
      defaultValue: STANDARD_WINDOW_TYPE,
      annotationTypes: [AnnotationType.OPENING_SKETCH],
      description: 'Window subtype, drawn with the matching floor-plan glyph',
      ui: {
        group: 'information',
        order: 4,
        hiddenInGroup: true,
        visibilityConditions: [{ propertyIdentifier: 'openingsketch_kind', value: OpeningKind.WINDOW }],
      },
      validation: { options: [...BUILTIN_WINDOW_TYPE_OPTIONS] },
    });
  }

  if (!propertyRegistry.isRegistered('openingsketch_hinge_at_start')) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_GLOBAL,
      identifier: 'openingsketch_hinge_at_start',
      name: 'hinge at start',
      valueType: PropertyValueType.BOOLEAN,
      defaultValue: true,
      annotationTypes: [AnnotationType.OPENING_SKETCH],
      description: 'Door hinge at the start end of the opening (else the far end)',
      // Hidden from the panel — driven by the on-glyph hinge toggle handle.
      ui: {
        group: 'information',
        order: 2,
        hidden: true,
        visibilityConditions: [
          { propertyIdentifier: 'openingsketch_kind', value: OpeningKind.DOOR },
          { propertyIdentifier: 'openingsketch_door_type', value: DoorType.OPEN_DOORWAY, negate: true },
        ],
      },
    });
  }

  if (!propertyRegistry.isRegistered('openingsketch_swing_positive')) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_GLOBAL,
      identifier: 'openingsketch_swing_positive',
      name: 'side',
      valueType: PropertyValueType.BOOLEAN,
      defaultValue: true,
      annotationTypes: [AnnotationType.OPENING_SKETCH],
      description: 'Which side of the wall the opening symbol faces (door swing / window shutter side)',
      // Hidden from the panel — driven by the on-glyph Side toggle handle. Also
      // chooses which wall face the `inset` (perp offset) applies to.
      ui: {
        group: 'information',
        order: 3,
        hidden: true,
        visibilityConditions: [{ propertyIdentifier: 'openingsketch_door_type', value: DoorType.OPEN_DOORWAY, negate: true }],
      },
    });
  }

  if (!propertyRegistry.isRegistered('openingsketch_rollable')) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_GLOBAL,
      identifier: 'openingsketch_rollable',
      name: 'rollable',
      valueType: PropertyValueType.BOOLEAN,
      defaultValue: false,
      annotationTypes: [AnnotationType.OPENING_SKETCH],
      description: 'Render this window as a rollable (roller-shutter) window',
      ui: {
        group: 'information',
        order: 5,
        hiddenInGroup: true,
        visibilityConditions: [
          { propertyIdentifier: 'openingsketch_kind', value: OpeningKind.WINDOW },
          { propertyIdentifier: 'openingsketch_window_type', value: STANDARD_WINDOW_TYPE },
        ],
      },
    });
  }

  // --- linkage back to the source floorplan OPENING ---
  if (!propertyRegistry.isRegistered('openingsketch_source_opening_id')) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_GLOBAL,
      identifier: 'openingsketch_source_opening_id',
      name: 'source opening',
      valueType: PropertyValueType.STRING,
      defaultValue: '',
      annotationTypes: [AnnotationType.OPENING_SKETCH],
      description: 'Identifier of the floorplan OPENING this opening sketch was replicated from',
      ui: { hidden: true },
    });
  }
}

/** The built-in door symbols of the `door type` dropdown (labels are translation keys). */
const BUILTIN_DOOR_TYPE_OPTIONS: readonly SelectOption[] = [
  { value: DoorType.SINGLE, label: 'single' },
  { value: DoorType.DOUBLE, label: 'double' },
  { value: DoorType.BIFOLD, label: 'bifold' },
  { value: DoorType.SLIDING, label: 'sliding' },
  { value: DoorType.BYPASS_SLIDING, label: 'bypass sliding' },
  { value: DoorType.POCKET, label: 'pocket' },
  { value: DoorType.SURFACE_SLIDING, label: 'surface sliding' },
  { value: DoorType.CLOSET, label: 'closet' },
  { value: DoorType.OPEN_DOORWAY, label: 'open doorway' },
];

const BUILTIN_WINDOW_TYPE_OPTIONS: readonly SelectOption[] = [{ value: STANDARD_WINDOW_TYPE, label: 'standard' }];

registerOpeningSketchProperties();

/** Re-register a select with other options (the registry stores configs by value). */
function replaceSelectOptions(identifier: string, options: SelectOption[]): void {
  const config: PropertyConfig | undefined = propertyRegistry.getConfig(identifier);
  if (!config) return;
  propertyRegistry.unregister(identifier);
  propertyRegistry.register({ ...config, validation: { ...config.validation, options } });
}

/**
 * Offer the organisation's doors / windows in the `door type` / `window type`
 * dropdowns, after the built-in symbols. Labels are the plain definition names
 * (not translation keys — the dropdown falls back to the text itself).
 * `archived` ones stay selectable for display only (openings already using
 * them keep their label) but are not offered.
 */
export function setOpeningSketchCustomTypeOptions(custom: { doors: ReadonlyArray<{ value: string; label: string; archived?: boolean }>; windows: ReadonlyArray<{ value: string; label: string; archived?: boolean }> }): void {
  const toOption = (o: { value: string; label: string; archived?: boolean }): SelectOption => ({ value: o.value, label: o.label, ...(o.archived ? { disabled: true } : {}) });
  replaceSelectOptions('openingsketch_door_type', [...BUILTIN_DOOR_TYPE_OPTIONS, ...custom.doors.map(toOption)]);
  replaceSelectOptions('openingsketch_window_type', [...BUILTIN_WINDOW_TYPE_OPTIONS, ...custom.windows.map(toOption)]);
}

function prop<T>(identifier: string, openingSketchId: string): ReactiveProperty<T> {
  const service = getGlobalPropertyService();
  return service.getProperty(identifier, openingSketchId, PropertyType.PROJECT_GLOBAL) as unknown as ReactiveProperty<T>;
}

export function getOpeningSketchWallEdgeIdProperty(id: string): ReactiveProperty<string> {
  return prop<string>('openingsketch_wall_edge_id', id);
}
export function getOpeningSketchOffsetProperty(id: string): ReactiveProperty<number> {
  return prop<number>('openingsketch_offset', id);
}
export function getOpeningSketchLengthProperty(id: string): ReactiveProperty<number> {
  return prop<number>('openingsketch_length', id);
}
export function getOpeningSketchLowerBoundProperty(id: string): ReactiveProperty<number> {
  return prop<number>('openingsketch_lower_bound', id);
}
export function getOpeningSketchHeightProperty(id: string): ReactiveProperty<number> {
  return prop<number>('openingsketch_height', id);
}
export function getOpeningSketchInsetProperty(id: string): ReactiveProperty<number> {
  return prop<number>('openingsketch_inset', id);
}
export function getOpeningSketchKindProperty(id: string): ReactiveProperty<OpeningKind> {
  return prop<OpeningKind>('openingsketch_kind', id);
}
export function getOpeningSketchDoorTypeProperty(id: string): ReactiveProperty<DoorGlyphType> {
  return prop<DoorGlyphType>('openingsketch_door_type', id);
}
export function getOpeningSketchWindowTypeProperty(id: string): ReactiveProperty<WindowGlyphType> {
  return prop<WindowGlyphType>('openingsketch_window_type', id);
}
export function getOpeningSketchHingeAtStartProperty(id: string): ReactiveProperty<boolean> {
  return prop<boolean>('openingsketch_hinge_at_start', id);
}
export function getOpeningSketchSwingPositiveProperty(id: string): ReactiveProperty<boolean> {
  return prop<boolean>('openingsketch_swing_positive', id);
}
export function getOpeningSketchRollableProperty(id: string): ReactiveProperty<boolean> {
  return prop<boolean>('openingsketch_rollable', id);
}
export function getOpeningSketchSourceOpeningIdProperty(id: string): ReactiveProperty<string> {
  return prop<string>('openingsketch_source_opening_id', id);
}
